import { Button, message, Modal, Spin, Typography } from "antd";
import { SyncOutlined } from "@ant-design/icons";
import React, { useEffect, useState } from "react";

import { controller } from "../../controller";
import SikkaPolicyReviewModal from "./SikkaPolicyReviewModal";

export default function SelectPolicyItemModal({ patientID, breakdownToSetPolicy, setShowSelectPolicyItemModal, showSelectPolicyItemModal }) {
  const [patientPolicies, setPatientPolicies] = useState([]);
  const [selectedPolicy, setSelectedPolicy] = useState(undefined);
  const [isGettingPatientPolicies, setIsGettingPatientPolicies] = useState(false);
  const [isSettingPatientPolicy, setIsSettingPatientPolicy] = useState(false);
  const [isFetchingFromSikka, setIsFetchingFromSikka] = useState(false);
  const [sikkaPreview, setSikkaPreview] = useState(null);

  useEffect(
    function () {
      async function getPatientPolicies() {
        if (!patientID) return;

        try {
          setIsGettingPatientPolicies(true);

          const response = await controller.getPatientPolicies(patientID);

          if (response.status === 200) {
            setPatientPolicies(response.json);
          }
        } catch (err) {
          console.error(err);
          message.error("Something went wrong while getting patient policies");
        } finally {
          setIsGettingPatientPolicies(false);
        }
      }
      getPatientPolicies();
    },
    [patientID],
  );

  function handleCancelAgent() {
    setShowSelectPolicyItemModal(false);
  }

  async function refreshPatientPolicies() {
    if (!patientID) return;
    try {
      setIsGettingPatientPolicies(true);
      const response = await controller.getPatientPolicies(patientID);
      if (response.status === 200) setPatientPolicies(response.json);
    } catch (err) {
      console.error(err);
    } finally {
      setIsGettingPatientPolicies(false);
    }
  }

  async function handleFetchFromSikka() {
    if (!patientID) return;
    try {
      setIsFetchingFromSikka(true);
      const response = await controller.previewPatientPoliciesFromSikka(patientID);
      if (response.status < 250 && response.json && response.json.preview_token) {
        setSikkaPreview(response.json);
      } else if (response.json && response.json.error_type === "no_insurance_data") {
        message.info("PMS returned no insurance information for this patient.");
      } else {
        const err = (response.json && (response.json.error || response.json.detail)) || "Could not fetch from PMS";
        message.error(err);
      }
    } catch (err) {
      console.error(err);
      message.error("Something went wrong while fetching from PMS.");
    } finally {
      setIsFetchingFromSikka(false);
    }
  }

  function handleSikkaSaved() {
    setSikkaPreview(null);
    refreshPatientPolicies();
  }

  async function handleSelectPatientPolicy(policy) {
    try {
      setIsSettingPatientPolicy(true);

      const response = await controller.setBreakdownSetPatient({
        policy_id: policy.id,
        breakdown_id: breakdownToSetPolicy.id,
      });

      if (response.status < 250) {
        // getBreakdownsDetailsData();
        message.success("Policy set successfully");
        setShowSelectPolicyItemModal(false);
      }
    } catch (err) {
      console.log(err);
      message.error("Something went wrong while setting the patient id.");
    } finally {
      setIsSettingPatientPolicy(false);
    }
  }

  return (
    <Modal open={showSelectPolicyItemModal} onCancel={handleCancelAgent} footer={null} title="Select Policy Item">
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
        <Button
          icon={<SyncOutlined />}
          loading={isFetchingFromSikka}
          onClick={handleFetchFromSikka}
          style={{
            background: "#EEEDFA",
            color: "#6B43B5",
            borderColor: "#6B43B5",
          }}
        >
          Fetch from PMS
        </Button>
      </div>
      {isGettingPatientPolicies ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
          <Spin />
        </div>
      ) : patientPolicies.length > 1 ? (
        <div
          style={{
            position: "relative",
            height: 288,
            maxHeight: 288,
            display: "flex",
            flexDirection: "column",
            gap: 10,
            overflowY: "auto",
            padding: 10,
          }}
          className="custom-scroll"
        >
          {/* Loading while setting patient policy */}
          {isSettingPatientPolicy && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(0, 0, 0, 0.2)",
                backdropFilter: "blur(5px)",
                borderRadius: 5,
              }}
            >
              <Spin />
            </div>
          )}

          {patientPolicies.map((policy) => (
            <div
              key={policy.id}
              style={{
                width: "100%",
                boxShadow: selectedPolicy && policy.id === selectedPolicy.id ? "0 0 10px rgba(107, 67, 181, 0.88)" : "0 0 10px 0 rgba(0, 0, 0, 0.15)",
                padding: "10px 20px",
                borderRadius: 5,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
              onClick={() => {
                setSelectedPolicy(policy);
                handleSelectPatientPolicy(policy);
              }}
            >
              <div>{policy.insurance_name_display}</div>
              <div>{policy.insurance_type_display}</div>
              <div>{policy.member_id}</div>
              <div>{policy.contract_number}</div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
          <Typography.Title level={5} style={{ marginBottom: 0, color: "#848696" }}>
            You have no policy
          </Typography.Title>
          {/* <Button onClick={() => setShowPatientInsuranceFormModal(true)} type="primary">
            Create Policy
          </Button> */}
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", justifyContent: "end", gap: 10, marginTop: 10 }}>
        <Button onClick={() => handleCancelAgent()}>Cancel</Button>

        {/* <Button type="primary" disabled={!(selectedPolicy && selectedPolicy.id)} onClick={() => handleScraperAgentCall()}>
          Fetch
        </Button> */}
      </div>

      <SikkaPolicyReviewModal
        open={!!sikkaPreview}
        preview={sikkaPreview}
        patientID={patientID}
        onClose={() => setSikkaPreview(null)}
        onSaved={handleSikkaSaved}
      />
    </Modal>
  );
}
